import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, CalendarCheck, MessageSquare, RefreshCcw } from "lucide-react";
import { Toaster, toast } from "sonner";
import { fetchAppointments, fetchEnquiries } from "../lib/api";

export default function AdminDashboard() {
  const token = typeof window !== "undefined" ? localStorage.getItem("ghh_admin_token") : null;
  const [tab, setTab] = useState("appointments");
  const [appts, setAppts] = useState([]);
  const [enqs, setEnqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [a, e] = await Promise.all([fetchAppointments(), fetchEnquiries()]);
      setAppts(a); setEnqs(e);
    } catch {
      toast.error("Session expired. Please sign in.");
      localStorage.removeItem("ghh_admin_token");
      nav("/admin/login");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, []);

  if (!token) return <Navigate to="/admin/login" replace />;

  const logout = () => { localStorage.removeItem("ghh_admin_token"); nav("/admin/login"); };

  return (
    <main data-testid="page-admin-dashboard" className="min-h-[80vh] bg-brand-cream py-12">
      <Toaster position="top-right" richColors />
      <div className="container-x">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl text-brand-navy-deep">Admin Dashboard</h1>
            <p className="text-sm text-slate-600 mt-1">Appointments and enquiries from the website.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="admin-refresh" className="btn-outline"><RefreshCcw className="w-4 h-4" /> Refresh</button>
            <button onClick={logout} data-testid="admin-logout" className="btn-primary"><LogOut className="w-4 h-4" /> Sign Out</button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-border overflow-hidden">
          <div className="flex border-b border-border">
            <button data-testid="tab-admin-appointments" onClick={()=>setTab("appointments")} className={`flex-1 px-6 py-4 text-sm font-semibold transition ${tab==="appointments" ? "text-brand-crimson border-b-2 border-brand-crimson" : "text-slate-600"}`}>
              <CalendarCheck className="w-4 h-4 inline mr-2" /> Appointments ({appts.length})
            </button>
            <button data-testid="tab-admin-enquiries" onClick={()=>setTab("enquiries")} className={`flex-1 px-6 py-4 text-sm font-semibold transition ${tab==="enquiries" ? "text-brand-crimson border-b-2 border-brand-crimson" : "text-slate-600"}`}>
              <MessageSquare className="w-4 h-4 inline mr-2" /> Enquiries ({enqs.length})
            </button>
          </div>

          <div className="overflow-x-auto">
            {tab === "appointments" ? (
              <table className="w-full text-sm" data-testid="table-appointments">
                <thead className="bg-brand-cream text-brand-navy-deep">
                  <tr>
                    {["Date","Name","Phone","Department","When","Message"].map(h=>(
                      <th key={h} className="text-left px-5 py-3 font-semibold text-xs uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td className="px-5 py-8 text-slate-500" colSpan="6">Loading…</td></tr>}
                  {!loading && appts.length === 0 && <tr><td className="px-5 py-8 text-slate-500" colSpan="6">No appointments yet.</td></tr>}
                  {appts.map((a)=>(
                    <tr key={a.id} className="border-t border-border align-top">
                      <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="px-5 py-4 font-semibold text-brand-navy-deep">{a.name}</td>
                      <td className="px-5 py-4">{a.phone}<div className="text-xs text-slate-500">{a.email}</div></td>
                      <td className="px-5 py-4">{a.department}</td>
                      <td className="px-5 py-4 whitespace-nowrap">{a.preferred_date}{a.preferred_time ? ` · ${a.preferred_time}` : ""}</td>
                      <td className="px-5 py-4 max-w-[280px] text-slate-600">{a.message || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-sm" data-testid="table-enquiries">
                <thead className="bg-brand-cream text-brand-navy-deep">
                  <tr>
                    {["Date","Name","Phone","Subject","Message"].map(h=>(
                      <th key={h} className="text-left px-5 py-3 font-semibold text-xs uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td className="px-5 py-8 text-slate-500" colSpan="5">Loading…</td></tr>}
                  {!loading && enqs.length === 0 && <tr><td className="px-5 py-8 text-slate-500" colSpan="5">No enquiries yet.</td></tr>}
                  {enqs.map((e)=>(
                    <tr key={e.id} className="border-t border-border align-top">
                      <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                      <td className="px-5 py-4 font-semibold text-brand-navy-deep">{e.name}</td>
                      <td className="px-5 py-4">{e.phone}<div className="text-xs text-slate-500">{e.email}</div></td>
                      <td className="px-5 py-4">{e.subject || "—"}</td>
                      <td className="px-5 py-4 max-w-[360px] text-slate-600">{e.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
